import React from 'react';
import { useTranslation } from 'react-i18next';
import { useClipboardCopy } from '@utils/clipboard';
import { confirmAlert } from '@services/confirmAlert';
import Turnstile from './Turnstile';

interface UploadActionsProps {
  isUploading: boolean;
  hasFiles: boolean;
  hasShareLink: boolean;
  hasError: boolean;
  shareLink?: string;
  onUpload: () => Promise<void>;
  onCancel: () => void;
  onSendMore: () => void;
  canUpload: boolean;
  canShowUpload: boolean;
  shareLinkNeedsConfirm?: boolean;
  onShareLinkAcknowledged?: () => void;
}

export function UploadActions({
  isUploading,
  hasFiles,
  hasShareLink,
  hasError,
  shareLink,
  onUpload,
  onCancel,
  onSendMore,
  canUpload,
  canShowUpload,
  shareLinkNeedsConfirm,
  onShareLinkAcknowledged
}: UploadActionsProps) {
  const { t } = useTranslation();
  const { copy, copyState } = useClipboardCopy();
  const [hasTurnstileToken, setHasTurnstileToken] = React.useState(false);

  const handleCopyShareLink = async () => {
    if (shareLink) {
      onShareLinkAcknowledged?.();
      await copy(shareLink);
    }
  };

  const handleUploadClick = () => {
    void onUpload();
  };

  const handleSendMoreClick = () => {
    if (hasShareLink && !isUploading && shareLinkNeedsConfirm) {
      confirmAlert({
        title: t('upload.shareLinkLossConfirm.title'),
        message: t(
          'upload.shareLinkLossConfirm.message',
          'If you leave this page now, the shareable link will be lost and cannot be recovered later. Continue?',
        ),
        buttons: [
          {
            label: t('upload.shareLinkLossConfirm.confirm', 'Continue'),
            onClick: () => {
              onSendMore();
            },
          },
          {
            label: t('upload.shareLinkLossConfirm.cancel', 'Stay on this page'),
          },
        ],
      });
      return;
    }

    onSendMore();
  };

  const shareLinkHighlightClass = hasShareLink && !isUploading && shareLink
    ? 'uploader-share-link-pulse'
    : '';

  // Show upload button when not uploading, have files, no share link, no errors,
  // and the user is allowed to start an upload (e.g., authenticated or accepted ToS)
  if (!isUploading && hasFiles && !hasShareLink && !hasError && canShowUpload) {
    return (
      <div className="upload-send-wrapper">
        <div className="auth-turnstile">
          <Turnstile
            onTokenReady={() => setHasTurnstileToken(true)}
            onInvalidated={() => setHasTurnstileToken(false)}
          />
        </div>
        <button
          type="button"
          onClick={handleUploadClick}
          aria-label={t('upload.encryptUpload')}
          disabled={!canUpload || !hasTurnstileToken}
        >
          {t('upload.encryptUpload')}
        </button>
      </div>
    );
  }

  // Show cancel button when uploading
  if (isUploading) {
    return (
      <div className="upload-send-wrapper">
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('common.cancel')}
        >
          {t('common.cancel')}
        </button>
      </div>
    );
  }

  // Show share link actions when upload is complete
  if (hasShareLink && !isUploading) {
    return (
      <>
        <input
          type="text"
          readOnly
          value={shareLink || ''}
          aria-label={t('share.title')}
          className={[
            'share-link-input',
            shareLinkHighlightClass
          ].filter(Boolean).join(' ')}
          onFocus={(e) => {
            onShareLinkAcknowledged?.();
            e.currentTarget.select();
          }}
        />
        <button
          onClick={handleCopyShareLink}
          aria-label={t('share.copy')}
          className={shareLinkHighlightClass}
        >
          {copyState === 'copied' ? t('share.copied') : t('share.copy')}
        </button>
        <button
          onClick={handleSendMoreClick}
          aria-label={t('common.sendMore')}
        >
          {t('common.sendMore')}
        </button>
      </>
    );
  }

  return null;
}
